const en = {
	common: {
		app_name: 'PubHubs',
		yivi: 'Yivi',
		loading: 'Loading ...',
		retry: 'Retry',
	},
	card: {
		description: 'Card issued with the following attributes:',
	},
	state: {
		hub_offline: 'No connection to this hub. What you see may be out of date.',
		rooms_offline: 'Rooms can be shown once there is a connection.',
		room_offline: 'This room can be shown once there is a connection.',
		button_request: 'Request access',
		initial: 'Checking whether hub client has access to cookies and local storage...',
		requesting: 'Requesting access...',
		denied: 'Sorry, the hub client cannot run without access to local storage',
		woops: 'Woops, I did not expect to find myself in this state.',
		loading: 'Loading...',
		loading_message: 'Loading message...',
	},
	dialog: {
		ok: 'Ok',
		cancel: 'Cancel',
		close: 'Close',
		yes: 'Yes',
		no: 'No',
		hub_pin_title: 'Pin hub',
		hub_unpin_title: 'Unpin hub',
		hub_unpin_context: 'Do you want to unpin this hub?',
		submit: 'Confirm',
		save: 'Save',
		ignore: 'Ignore',
		edit: 'Edit',
		continue: 'Continue',
		back: 'Back',
		next: 'Next',
		previous: 'Previous',
		title_sure: 'Are you sure?',
		go_back: 'Go back',
	},
	errors: {
		// Shown for things that need the servers (entering a hub for the first time, logging in) while
		// they can't be reached.
		needs_connection: 'This needs an internet connection. Check your connection and try again.',
		oops: 'OOPS!',
		error: 'Unfortunately an error occured. Please <a href="https://www.pubhubs.net/en/help/contact/" target="_blank" class="text-accent-primary">contact the developers</a>.\n\n {0}',
		server: 'Server error',
		M_LIMIT_EXCEEDED: 'Too much login attempts. Try again in {0} seconds.',
		not_send_offline: 'No internet connection. This message was not send.',
		resend: 'Send again',
		file_upload: 'File format not supported',
		file_upload_failed: 'File could not be uploaded',
		file_upload_unsupported_type: 'Files of type {type} cannot be uploaded here.',
		// Reached when the file passes our own check but is still refused, which happens when the
		// server in front of the hub has a lower limit than the hub itself and names no number.
		file_upload_too_large: 'The server refused this file because it is too large ({size}). Try a smaller file, or compress it first.',
		file_upload_too_large_max: 'This file is {size}, which is more than the maximum of {max}.',
		file_upload_read_failed: 'This file could not be read from your device. It may have been moved or renamed, please select it again.',
		file_upload_network: 'The file could not be sent. Check your internet connection and try again.',
		file_upload_rate_limited: 'Too many uploads in a short time. Please wait a moment and try again.',
		file_upload_forbidden: 'You are not allowed to upload files here. Try logging in again.',
		file_upload_server: 'The hub could not accept this file right now (error {status}). Please try again later.',
		file_upload_unknown: 'The file could not be uploaded (error {status}). Please try again.',
		file_download: 'This file could not be downloaded. Please try again.',
		file_share: 'This file could not be shared in the conversation. Please try again.',
		no_valid_attribute: 'A secured room needs to have valid attributes',
		cant_find_room: 'This room is not available at the moment',
		admin_error: 'You are not an admin!',
		no_room_error: 'User {0} has not joined any room in the hub.',
		// Reached on login, where a PubHubs card is the only thing disclosed - so it must not suggest
		// logging in with something else, or registering with the card. Registering starts over from
		// the attributes and hands out a card of its own.
		account_does_not_exist: 'We could not find a PubHubs account for this. If you do not have an account yet, register a new one.',
		// Reached both when registering and when logging in, so it says neither.
		attribute_banned: 'The following attribute is banned and cannot be used in PubHubs: {0}.',
		banned: 'This account is banned and can no longer be used to login to PubHubs.',
		attribute_already_taken: 'The following attribute is already tied to a PubHubs account: {0}. Try logging in.',
		general_error:
			'Unfortunately an error occured. Try again and <a href="https://www.pubhubs.net/en/help/contact/" target="_blank" class="text-accent-primary">contact the developers</a> if the problem persists.',
		// The server rejected a signed attribute as expired or invalid, which means disclosing again -
		// not adding anything in Yivi, which for a PubHubs card the user cannot do at all.
		retry_with_new_attr: 'Your details could not be verified, they may have expired. Please try again.',
		no_hubs_found:
			'Unfortunately we could not show any hubs. Try again later and <a href="https://www.pubhubs.net/en/help/contact/" target="_blank" class="text-accent-primary">contact the developers</a> if the problem persists.',
		cant_find: 'Sorry, we could not find the page or room that you were looking for.',
		notid_attribute_already_taken:
			'You have already used the phone number for another account, if you still want to create a new account, click ok and try again',
		notid_taken_title: 'Phone number already in use',
		YiviServerGone: 'You could likely not complete the login because battery-saver blocks javascript, try the last step one more time to log in.',
		// Shown for a Yivi session the widget cannot restart by itself: a chained session, which ends
		// there on a cancel and on a timeout as much as on an error. Battery-saver and a slow connection
		// both come out as one of those, and neither says anything about the retry - which no longer
		// chains, so it is worth taking.
		yivi_session_failed: 'Something went wrong in the Yivi app, so we could not log you in. Please try again.',
		// The card is on the account but never reached the user's Yivi app, and logging in later
		// discloses exactly that card - so the last step really does have to be finished.
		card_not_added:
			'You are logged in, but your PubHubs card was not added to your Yivi app. You need it to log in next time, so please finish the last step.',
		// The card reached the Yivi app but could not be tied to the account, which the next login
		// needs in order to recognise it. Issuing another one repairs this, so it asks for a retry too.
		card_not_linked:
			'You are logged in, but your PubHubs card could not be linked to your account. You need it to log in next time, so please try the last step again.',
	},
	file: {
		upload: 'Upload',
		file: 'File',
		upload_file: 'Upload file',
		upload_files: 'Upload files',
		upload_message: 'Do you want to upload',
		remove_all_files: 'Remove all',
		upload_error: 'Upload error, try again.',
		from_library: 'From files',
	},
	forms: {
		next: 'Next step',
		skip: 'Skip',
		back: 'Back',
		submit: 'Submit',
		save: 'Save',
	},
	home: {
		hub_homepage_welcome: 'Welcome to the {0}-hub!',
		hub_homepage_join: 'Join this hub',
		hub_homepage_welcome_auth: 'Welcome to the {0}-hub.',
		welcome: 'Welcome',
		highlighted_hubs: 'Highlighted hubs',
		welcome_to: '{0} Hub overview',
		hub_card_about: 'About',
		heading: 'Hub description:',
		contact_details: 'Contact details:',
		enter_hub: 'Enter {hub}',
		hubs_overview: 'Hubs overview',
		pinned: 'Pinned',
		discover: 'Discover',
		add_hub: 'Add hub',
		add_hub_description: 'Discover hubs and pin your first one.',
		pinned_all_hubs: 'You already pinned all hubs',
		no_hubs_available: 'No hubs are available',
		load_hubs_failed: 'The hubs could not be loaded.',
	},
	about: {
		title: 'About PubHubs',
		version: 'Version {0}',
		description: 'PubHubs is a public, privacy-friendly and open-source community network.',
		help_button: 'Go to help',
	},
	mss: {
		system_offline: 'System offline',
		system_offline_description:
			'Our system is offline right now, so not everything works as expected.\nPlease try again later. <a href="https://www.pubhubs.net/en/help/contact/" target="_blank" class="text-accent-primary">Contact the developers</a> if the problem persists.',
	},
	register: {
		have_account: 'Do you already have a {0} account?',
		no_account_yet: 'No {0} account yet?',
		register_3_steps: 'Register for {0} in 3 steps!',
		card_1_title: 'Secure registration with {0}',
		card_1_text_1: '{0} uses a separate app for logging in and registration: {1}.',
		card_1_yivi: 'What is {0}?',
		card_1_yivi_text: '{0} is an identity app that allows you to log in easily and securely, share information — without oversharing personal data.',

		card_2_title: 'Add your email and phone number to {0}',
		card_2_text_1: 'Follow the steps in the {0} app. Then, add your phone number.',
		card_2_text_2: '{0} needs this information to complete registration.',

		card_3_title: 'Scan the QR code',
		// The _app variants are shown on a device that opens the Yivi app by link, where there is no
		// QR code to scan.
		card_3_title_app: 'Open the {0} app',
		card_3_text_2: 'Scan the QR code with your {0} app and share your details to log into {1}.',
		card_3_text_2_app: 'Open your {0} app and share your details to log into {1}.',
		// The success variants are shown once the disclosure succeeded and the card is being issued.
		card_3_success_title: "You're logged in!",
		card_3_success_text: 'Scan the {0} Qr code once more to add your {1} card.',
		card_3_success_text_app: 'Open your {0} app once more to add your {1} card.',
		card_3_success_text_chained: 'Add your {1} card in the {0} app you just used to finish.',

		card_1_alt: 'Laptop',
		card_2_alt: '{0} mascot with attributes',

		video_click_to_play: 'Click to play video',
		video_hosted_by_vimeo: 'This video is hosted by Vimeo',

		yivi_explained: '{0} explained in 1,5 minutes',
		yivi_faq: 'Frequently asked questions',
		yivi_faq_1_question: 'What is Yivi?',
		yivi_faq_1_answer: 'Yivi is an identity app that allows you to log in securely and share data without revealing too much personal information.',
		yivi_faq_2_question: 'How does Yivi work?',
		yivi_faq_2_answer: 'Yivi uses attributes you can present when a website or app requires them, without having to share everything about yourself.',
		yivi_faq_3_question: 'Why does PubHubs use Yivi?',
		yivi_faq_3_answer: 'PubHubs uses Yivi to securely verify your identity and ensure your privacy.',
		yivi_faq_4_question: 'How do I add data to Yivi?',
		yivi_faq_4_answer: 'You can add personal data via the Yivi app by retrieving it from trusted sources such as the government or your employer.',
		yivi_faq_5_question: 'Why does PubHubs need my email address and phone number?',
		yivi_faq_5_answer: 'This information is needed to guarantee that you are a unique individual.',

		register: 'Register',
		register_with: 'Register with {0}',
		welcome: 'Welcome, join Pubhubs!',
		use_yivi: 'To register, you will use the Yivi app\n(and for logging in as well, by the way).',
		start_registration: 'You start registering here, in 3 steps.',
		overview_step1: "Download the Yivi app — if you don't already have it",
		overview_step2: 'Add your data to the Yivi app',
		overview_step3: 'Scan the QR code with the Yivi app',
		yivi_downloaded: 'Do you already have the Yivi app?',
		yivi_no: "No \xa0\xa0I don't have the Yivi app",
		yivi_yes: 'Yes \xa0\xa0I have the Yivi app',
		skip_step1: 'Skip step 1',
		step_go_to: 'Go to step {0}',
		step_previous: 'Back to the previous step',
		install_yivi: 'Install the Yivi app.',
		yivi_what: 'What is Yivi?',
		yivi_short_description: 'An app that allows you to easily and securely prove who you are.',
		yivi_download: 'Download the Yivi app using one of the black buttons and follow the instructions in the app.',
		yivi_installed: 'I have installed the Yivi app now',
		more: 'Want to know more?',
		yivi_workings: 'How does Yivi work?',
		yivi_workings_answer:
			'Yivi is an app for your digital identity. You can use Yivi to share personal data about yourself, such as your name, address, age, phone number and email address. You share only the data that is necessary in a particular situation. You can use Yivi to log into several websites and make yourself known there.\n\nTo use Yivi, you first have to add data about yourself in the app. It is explained in the app how to do this. For more information, see the website {hyperlink}. To use Yivi for PubHubs, you have to add an email address and phone number in the app.',
		yivi_why: 'Why Yivi for PubHubs?',
		yivi_why_answer:
			'Within PubHubs, Yivi is used for two things. {reasons} Yivi, like PubHubs, is privacy-friendly and open about how it works, via open-source software. Yivi is a separate app used by PubHubs, but not part of PubHubs. Yivi is also used on other websites for logging in.',
		yivi_why_central_login:
			'For central login. You then share your email address and your mobile number. Those data are only known at the central login and not in the different hubs you visit. Your data is not shared with others.',
		yivi_why_room_login:
			'To log in to some rooms, where more information is requested about the participants. This may happen, for example, in a room for people in your neighborhood, where you must first prove what your zip code is to get in. You may also have to prove that your email address is on a list for a particular room before you can get in.',
		yivi_link: 'https://yivi.app/en/',
		data_request: 'PubHubs asks for an email address and mobile number, just for registration.',
		data_add: 'Add your email address and mobile number in the Yivi app.',
		data_add_how: 'How do I add this data?',
		data_add_how_answer:
			'There is a button in the Yivi app that allows you to collect personal information and save it in the app. For PubHubs, you need to add an email address and a mobile number in your Yivi app. You can add multiple email addresses and phone numbers. You can then choose which one you wish to use to sign in to PubHubs.\n\nThis way, if you like, you can have two different accounts for PubHubs: a private account, with your private email and mobile number, and one for work, with your work email and number.',
		data_why: 'Why does PubHubs need my email and number?',
		data_why_answer:
			'PubHubs uses the combination of your email and mobile number to recognize you. This data is not used to contact you. If you have a new phone, you will need to sign up with PubHubs again. In that case, when signing up, use your email address and mobile number that you used to log in previously. Then PubHubs will recognize you and you can continue your previous conversations.',
		data_added: 'I have now added this data',
		data_share: 'Share your email and mobile number in the app',
		add_registration: 'Add your PubHubs registration in the app',
		rewards: {
			good_job: 'Good job!',
			almost_done: "Great, you're almost done!",
			hooray: 'Hooray! {0} You are registered and logged in to PubHubs.\nYou will be redirected to an overview of hubs automatically. Have fun!',
		},
		inapp: {
			in_yivi: 'You are likely viewing this page from within the Yivi app.',
			reobtain_not_possible: '(Re)obtaining a PubHubs card is not currently not possible from there.',
			go_to_pubhubs_directly: 'Please go to PubHubs website via your browser, and (re)register there.',
			youll_get_card: 'You will receive a PubHubs card in this process.',
		},
	},
	login: {
		login: 'Login',
		mascot_alt: '{0} mascot',
		login_again: 'You have been logged out. Please log in again to continue.',
		loading_yivi: 'Loading Yivi...',
		yivi_app_missing: 'Could not open the Yivi app. Please make sure it is installed, then try again.',
	},
	review_mode: {
		enter_title: 'Switch to the review environment?',
		enter_text:
			'This is for app store reviewers. PubHubs then uses a separate test environment, where you register with Yivi demo credentials. You can switch back on the login page.',
		label: 'Review environment',
		leave: 'Leave',
	},
	logout: {
		logout: 'Logout',
		logout_sure: 'Are you sure you want to logout?',
	},
	pwa: {
		add_app: 'Add PubHubs as app',
		click_menu: 'Tap the menu icon',
		add_to_homescreen: 'Tap "Add to Home screen"',
		add_page_to: 'Tap "Add page to" and choose "Home screen"',
		add_to: 'Tap "Add to..." and choose "Home screen"',
		add_to_phone: 'Tap "Add to phone"',
		add_manually: 'Add manually:',
		install: 'Install',
		click_share: 'Tap the "Share" icon',
		ios_add_to_homescreen: 'Swipe up and tap "Add to Home Screen"',
		do_not_show_again: 'Do not show again on this device*',
		find_instructions: '* You can look at these instructions again from the settings.',
		open_instructions: 'Look at instructions',
	},
	menu: {
		calender: 'Calender',
		contact: 'Contact Admin',
		copy_hub_url: 'Copy hub-url',
		copy_room_url: 'Copy room-url',
		enter_room: 'Enter room',
		leave_room: 'Leave room',
		leave_conversation: 'Leave conversation',
		download_file: 'Download',
		hide_message: 'hide message',
		show_message: 'show message',
		copy_image: 'Copy image',
		save_image: 'Save image',
		home: 'Hub welcome',
		directmsg: 'Direct messages',
		discover: 'Discover rooms',
		logout: 'Logout',
		name: 'Here will be a name',
		private_rooms: '@:rooms.private_rooms',
		group_rooms: 'Group rooms',
		admin_contact: 'Admin Contact Rooms',
		rooms: '@:rooms.rooms',
		settings: 'Settings',
		tool: 'Tool',
		admin_tools: 'Admin tools',
		admin_tools_rooms: 'Manage rooms',
		admin_tools_room: 'Manage room: {0}',
		admin_tools_users: 'Manage users',
		admin_tools_hub_settings: 'Hub settings',
		admin_tools_roles: 'Roles',
		back_to_rooms: 'Back to rooms',
		close_admin_menu: 'Close admin menu',
		overview: 'Overview',
		moderation: 'Moderation',
		manage: 'Manage',
		admin_dashboard: 'Admin dashboard',
		steward_dashboard: 'Steward dashboard',
		reports: 'Reports',
		audit_log: 'Audit log',
		add_reaction: 'Add reaction',
		copy_message: 'Copy message',
		edit_message: 'Edit message',
		delete_message: 'Delete message',
		direct_message: 'Direct message',
		whisper: 'Whisper',
		join_room: 'Join',
		reply: 'Reply',
		reply_in_thread: 'Reply in thread',
	},
	others: {
		step: 'Step {0}',
		nop: 'This feature is not implemented yet.',
		search: 'Search',
		search_rooms: 'Search for rooms',
		search_hubs: 'Search for hubs',
		search_users: 'Search for users',
		search_rooms_not_found: 'No rooms were found',
		search_room: 'Search this room',
		search_room_hint: 'Enter a search term and press Enter to search messages in this room.',
		search_conversation: 'Search this conversation',
		room_members: 'Members',
		searching: 'Searching...',
		search_nothing_found: 'No results found',
		clear_search: 'Clear search',
		search_found_items: 'Number of found items:',
		empty_users: 'No users to manage',
		read_receipt: 'Read by',
		new_message: 'New conversation',
		join_room_to_dm: 'No users found. Join a room first to talk to other users.',
		new_group: 'New group',
		group_select: 'Select maximum of 5 people',
		next: 'Next',
		group_name: 'Group name',
		select_group_name: 'Select group name',
		group_members: 'members',
		typing: 'Start typing...',
		load_more_results: 'Load more results',
		help: 'Are you struggling with using PubHubs?',
		work_in_progress:
			'PubHubs is a work in progress. A manual is currently being worked on. In the meantime, please ask your question in one of the rooms.',
		no_private_message: "It's looks a little quiet here. Lets start something new! Just tap the + icon to begin your first conversation.",
		select_conversation: 'Select a conversation to start messaging',
		open_conversation: 'Open conversation with {name}',
		start_new_conversation: 'Start a new conversation using the + button',
		filter_users: 'filter',
		filter_values: 'Type to search',
		deselect: 'Deselect',
		clear_selection: 'Clear selection',
		toggle_options: 'Show options',
		select_value: 'select value',
		userId: 'User ID',
		page_x_of_y: 'Page {0} of {1}',
	},
	hubs: {
		under_construction: 'This hub is under construction',
	},
	rooms: {
		room: 'Room',
		thread: 'Thread',
		me: 'Me',
		just_you: 'Just you',
		join_sure: 'Do you wish to join this room?',
		leave_sure: 'Do you really want to leave this room?',
		leave_admin: 'Warning: You are the only admin in the room. If you leave the room, there will be no admin.',
		leave_only_admin: 'Warning: You are the creator & only member of the room. If you leave, theroom will be deleted.',
		group_limit: 'You cannot add more users to the group',
		leave_dm_sure: 'Do you really want to leave this conversation? You will lose access to the message history.',
		try_again: 'Cannot join room. Please try again.',
		new_message: 'Message',
		members: 'members',
		member: 'member',
		private_room: 'Private room',
		private_members: 'With ',
		private_rooms: 'Private rooms',
		private_topic: 'A conversation between',
		private_add: 'New private room',
		private_search_user: 'Search person',
		rooms: 'Rooms',
		join_room: 'Join room',
		already_joined: 'Enter room',
		view_access_requirements: 'View requirements',
		close_access_requirements: 'Close requirements',
		join_secured_room: 'Join room',
		join_secured_room_dialog: 'Join the secured room using yivi.',
		joined: 'Room successfully joined!',
		add_room: 'Add a room',
		name: 'Name of the room',
		contact: 'Room Steward for room ',
		name_general_room: 'General',
		name_feedback_room: 'Your feedback on PubHubs',
		no_match: 'No rooms were found that match your search query.',
		filter: 'Search rooms',
		title: '{0}',
		access_denied: 'Access Denied',
		secure_room_message_heading: 'Before you can enter the room',
		secure_room_message: 'To enter scan the QR code with your Yivi app',
		incorrect_attributes:
			'The information you shared does not give you access to this room. Check that you used the right details in your Yivi app and try again.',
		required_attributes:
			'This is a secured room. To join, scan the QR code below with your Yivi app. You will share only the following information with the room, nothing else:',
		display_qr: 'Display QR-code',
		secure_room_enter_info: 'Please show the following:',
		secured_room_error: 'We are sorry, but it seems like you cannot access the room! Please check you gave the correct data with the Yivi App.',
		secured_room_attributes_edit_error: 'Add at least one value per attribute',
		unavailable: 'There are no available rooms to join',
		admin_badge: 'Admin',
		member_count: 'Member count',
		yivi_attributes: 'Yivi attributes',
		room_type_prefix_public: 'public',
		room_type_prefix_secured: 'secured',
		room_type_suffix_default: 'room',
		room_type_suffix_forum: 'forum room',
		upload_error: 'Error while uploading',
		upload_not_allowed: 'This filetype is not allowed.',
		empty_rooms: 'No rooms to manage',
		latest_news: 'Latest news',
		more_news: 'More news',
		more_suggestions: 'More suggestions',
		popular_rooms: 'Popular rooms and discussions',
		watch: 'Watch',
		read: 'Read',
		event: 'Event',
		discussion: 'Discussion',
		roomCreated: 'Room created',
		no_messages_yet: 'There are no messages in this room yet',
		preview_image: 'Image',
		preview_file: 'File',
		preview_poll: 'Poll',
		preview_signed: 'Signed message',
		preview_unknown: 'Sent a message',
		discover: 'Discover rooms',
		room_library: 'Library for room:',
		no_rooms_found: 'No rooms were found',
		load_failed: 'The rooms could not be loaded.',
		unread_messages: 'Unread Messages',
		last_read_message: 'Last read',
		one_new_message: '1 new message',
		new_messages: '{count} new messages',
		scroll_to_new_messages: 'Scroll to {count} new messages',
		jump_to_bottom: 'Jump to bottom',
		jump_to_unread_thread: 'Unread thread',
		loading_older_messages: 'Loading older messages...',
		loading_newer_messages: 'Loading newer messages...',
		announcement: 'announcement',
		steward: 'steward',
		stewards: 'stewards',
		experts: 'experts',
		steward_support: 'Steward Contact',
		contact_steward_title: 'Contact room steward',
		contact_steward_subtitle: 'For room-related questions',
		memberlist: 'Member list',
		profile_public_rooms: 'Public rooms in common',
		profile_shared_secured_rooms: 'Shared secured rooms',
		administrator: 'administrator',
		votes: 'votes',
	},
	settings: {
		profile_title: 'Your profile',
		edit_profile: 'Edit profile',
		change_avatar: 'Change picture',
		remove_avatar: 'Remove picture',
		confirm_remove_avatar: 'Are you sure you want to remove your profile picture?',
		userId_description: 'This is your unique ID for this hub. You cannot change this.',
		userId: 'Unique hub ID',
		displayname: 'Chosen name',
		avatar: 'Picture',
		avatar_changed: 'Picture updated!.',
		displayname_changed: 'Name changed to `{0}`.',
		theme: 'Colorscheme',
		theme_changed: 'Colorscheme changed to `{0}`.',
		timeformat: 'Time notation',
		language: 'Language',
		language_changed: 'Language changed to `{0}`.',
		title: 'Settings',
		notifications: 'Notifications',
	},
	hub_settings: {
		heading: 'Hub settings',
		icon_heading: 'Hub icon',
		icon_description: 'A public image for this hub. Use a PNG, JPEG or SVG file, max 5 MB.',
		save: 'Save',
		settings_saved: 'Changes saved',
		error_saving_icon: 'Could not save the hub log. Please try again later.',
		error_saving_banner: 'Could not save the hub banner. Please try again later.',
		file_format_not_allowed: 'Filetype not allowed. Please use .png, .jpeg, .jpg or .svg files.',
		file_too_large: 'File too large (max 5 MB). Please pick a smaller file.',
		description_heading: 'Hub description',
		description_description: `Choose a clear and concise description of the topic and the target audience of this hub. This description will be displayed on the 'Hub welcome' page within the hub.`,
		description: 'The hub is for .... The target audience of the hub includes ....',
		banner_heading: 'Hub banner',
		banner_description: 'A publicly visible banner image for this hub (468x85)px. Use a PNG, JPEG or SVG file, max 5 MB.',
		summary_heading: 'Hub summary',
		summary_description: `This description will appear on the central 'Discover hubs' page under the hub name and is searchable.`,
		summary: 'Online community / platform for ....',
		contact_heading: 'Hub contact details',
		contact_description: 'Contact details of the owners of the hub.',
		contact: 'Your email address',
		consent_heading: 'Hub houserules',
		consent_description: 'The houserules that every user has to accept to enter the hub.',
		consent: 'Fill in the rules every user in your hub has to adhere to',
	},
	admin: {
		title: 'Admin tools',
		manage_room_description: 'Create, edit, and delete (secured) rooms',
		manage_user_description: 'Search and manage user access control',
		add_room: 'Add a public room',
		add_secured_room: 'Add a secured room',
		name: 'Name',
		name_placeholder: 'Name of the room',
		topic: 'Description',
		topic_placeholder: 'Description of the room topic',
		public_room: 'Public room',
		public_rooms: 'Public rooms',
		secured_room: 'Secured room',
		secured_rooms: 'Secured rooms',
		secured_rooms_tooltip: 'To access secured rooms, users must share certain information via the Yivi app.',
		room_type: 'Room type',
		room_type_placeholder: 'Normal room',
		room_type_forum: 'Forum room',
		room_type_locked: 'The room type cannot be changed after the room has been created.',
		edit_name: 'Change room name',
		room_category: 'Room category',
		room_details: 'Room details',
		user_details: 'User details',
		report_details: 'Report details',
		select_room_placeholder: 'Select a room from the list to see more details',
		select_user_placeholder: 'Select a user from the list to see more details',
		select_report_placeholder: 'Select a report from the list to see more details',
		filter_reports: 'Filter reports',
		empty_reports: 'No reports found',
		reported_message: 'Message',
		report_room: 'Room',
		report_sender: 'Reported user',
		report_reporter: 'Reported by',
		report_reason: 'Reason',
		report_score: 'Score',
		report_time: 'Time',
		report_event_id: 'Event ID',
		preview_message: 'View message',
		message_preview: 'Message preview',
		message_type_image: '[Image]',
		message_type_file: '[File]',
		message_content_unavailable: 'Message content unavailable',
		message_not_found: 'Message not found',
		go_to_message: 'Go to message',
		report_from: 'Reported by',
		report_against: 'Against',
		no_reason: 'No reason provided',
		no_score: 'No score',
		delete_report: 'Delete report',
		delete_report_confirm: 'Are you sure you want to delete this report?',
		load_more_reports: 'Load more ({loaded} of {total})',
		reports_since: 'Since {0}',
		load_older: 'Load older',
		message: 'Message',
		report: 'report',
		reports: 'reports',
		go_to_room: 'Go to room',
		edit_room: 'Edit room',
		edit_secured_room: 'Edit secured room',
		remove_room: 'Remove room',
		upgrade_to_steward: 'Upgrade to steward',
		added_room: 'Room added',
		no_rooms: 'No rooms',
		remove_room_sure: 'Are you sure to delete this public room?',
		no_secured_rooms: 'No secured rooms',
		secured_remove_sure: 'Are you sure to delete this secured room?',
		secured_description: 'Attributes Description',
		secured_description_placeholder: 'Description of attributes needed to join the room',
		expiration_days: 'Expiration time (days)',
		expiration_days_placeholder: 'Number of days before users need to re-authenticate (1-365)',
		secured_yivi_attributes: 'Yivi Attributes',
		secured_attribute: 'Attribute',
		secured_values: 'Values',
		secured_profile: 'Show attribute in badge in the room',
		ask_disclosure_title: 'Disclosure Request',
		dm_user: 'Direct message',
		ask_disclosure_choose_user: 'Ask a user to disclose information',
		ask_disclosure_user_title: 'User',
		ask_disclosure_purpose_title: 'Promotion type',
		ask_disclosure_where_title: 'Disclose to',
		ask_disclosure_where_room_title: 'Disclose to room',
		ask_disclosure_where_room_placeholder: '!room:...',
		ask_disclosure_where_public: 'a public room',
		ask_disclosure_where_private: 'the steward',
		ask_disclosure_yivi_message: 'An admin or steward asked you to share the following attributes',
		ask_disclosure_message_title: 'Message',
		ask_disclosure_message_placeholder: 'Please disclose the following information',
		ask_disclosure_message_to_recipient: 'To {0}:\n\n{1}\n\nThe following information is requested: {2}',
		disclosure_sign_message: 'The user disclosed some information:',
		disclosure_dialog_title: 'You have been asked to disclose some information',
		disclosure_message: 'The attributes you disclose will be shared to the follwing room: {0}',
		disclose: 'Click to disclose',
		private_room: 'private room',
		this_room: 'this room',
		room_admin_success: 'Administrator',
		room_admin_message: 'User is now room admin',
		make_admin: 'Do you want to make yourself an admin of the room?',
		user_permission_message: 'Do you want to change the permissions?',
		permission_success: 'Permissions changed successfully',
		join_warning: 'No room admins were found. To assign administrative privileges for this room, click Join.',
		refresh_admin_operation: 'Refresh the page if the admin badge is missing.',
		user_perm_heading: 'User permissions',
		user_perm_public_only: 'Roles in public rooms; chat rooms are not listed.',
		user_perm_no_rooms: 'This user has not joined any public rooms.',
		user_perm_room_admin_locked: 'Room admins cannot be changed here.',
		user_perm_saved: 'Saved',
		not_admin_perm_msg: 'You need to be room admin to change permission. Permission not changed!',
		title_room: 'Room',
		title_room_join: 'Room Join',
		title_permission: 'Permission',
		title_update: 'Update',
		title_user: 'User',
		title_administrator: 'Administrator',
		title_room_administrator: 'Room Administrator',
		title_hub_administrator: 'Hub Administrator',
		title_steward: 'Steward',
		title_room_steward: 'Room Steward',
		make_admin_confirm: 'Make Admin',
		join: 'Join',
		reload: 'Reload',
		information: 'Information',
		join_admin_title: 'Join Local Admin',
		join_admin_text: 'Click to join local admin',
		make_admin_title: 'Make Admin',
		make_admin_text: 'Make yourself room admin',
		flow_complete_title: 'Completed',
		flow_complete_text: 'You are now room admin',
		support: 'Admin support',
		remove_warning: 'If you remove a value or add a new attribute all users will have to rejoin the room',
		add_value: 'Add value',
		add: 'Add',
		add_tip: 'Use "," to add several values at once',
		editroom_typing: 'Select here the secured attribute you want to check',
		value: 'Values',
		admin_contact_title: 'Contact hub administrator',
		admin_contact_subtitle: 'For hub-wide issues only',
		admin_contact_main_msg:
			'For room-related questions, please contact the room steward first. You can find the steward contact in the members panel of the room. The hub administrator is only for hub-wide issues or when the steward cannot help. Do you want to continue?',
		if_admin_contact_not_present:
			'The Hub Administrator is not available at the moment. For room-related questions, you can contact the room steward via the members panel in the room.',
		rooms_you_moderate: 'Rooms you moderate',
		you_suffix: '(you)',
	},
	moderation: {
		admins: 'admins',
		yellow_card: 'yellow card',
		yellow_card_info: 'you have received a yellow card as a warning for this room. reason: ',
		issue_yellow_card: 'issue a yellow card',
		issue_yellow_card_info: 'the member will receive a warning',
		revoke_yellow_card: 'revoke yellow card',
		red_card: 'red card',
		red_card_info: 'you have received a red card and are no longer able to enter this room. reason: ',
		issue_red_card: 'issue a red card',
		issue_red_card_info: 'the member will be barred from the room',
		revoke_red_card: 'revoke red card',
		card_reason_label: 'reason for issuing this card',
		sanctioned_members: 'sanctioned members',
		hidden_message: 'hidden',
		hide_message: 'hide',
		hide_message_info: 'the selected message will be hidden with your reason as warning',
		hide_reason_label: 'reason for hiding this message',
		remove_from_room: 'remove from room',
		remove_from_room_info: 'the member {name} will be removed from this room',
		removed_from_room: 'you have been removed from the room, do you want to rejoin?',
		kick_reason_label: 'reason for removing this member',
		kick_reason: 'reason',
		issue_timeout: 'issue a timeout',
		issue_timeout_info: 'temporarily prevent {name} from sending messages',
		revoke_timeout: 'revoke timeout',
		timeout_hours: 'hours',
		timeout_minutes: 'minutes',
		timeout_reason_label: 'reason for issuing this timeout',
		report_message: 'report message',
		report_message_info: 'report this message to the stewards of this room',
		report_reason_label: 'reason for reporting this message',
		you_are_timed_out: 'timed out:',
		timeout_expires_in: 'expires in {time}',
		timeout_reason: 'reason',
		cannot_send_timed_out: 'you cannot send messages while timed out',
		timed_out_members: 'timed out members',
		you_have_warning: 'warning:',
		dismiss_warning: 'dismiss',
		warned_members: 'warned members',
		placeholder_timed_out: 'You cannot type until timeout has passed',
		placeholder_warning: 'Dismiss warning to type',
	},
	themes: {
		dark: 'Dark',
		light: 'Light',
		system: 'System',
	},
	timeformats: {
		format12: '12 hours',
		format24: '24 hours',
	},
	notifications: {
		allow: 'On',
		deny: 'Off',
		notSupported: 'This browser does not support notifications.',
		promptAllow: 'Notifications in your browser are blocked. Open the permissions in your browser to allow PubHubs notifications.',
		promptReset: 'To stop receiving notifications, you need to withdraw your permission in the browser preferences.',
		info: `Continue in your browser: Click 'allow' in the pop-up on your screen to receive PubHubs notifications in your browser.`,
		heading: 'Notifications',
		dismiss: 'Dismiss',
		removed_from_secured_room:
			'You have been removed from the secured room {0} because {1} days have passed or someone has modified the room; use Yivi to re-enter the room.',
		soon_removed_from_secured_room: 'You will be removed from the secured room {0} within {1} days; use Yivi to stay in the room.',
		rejoin: 'rejoin',
		rejoin_secured_room: 'Rejoin room',
	},
	onboarding: {
		house_rules: 'House rules {0}',
		welcome: 'Welcome at the {0} hub!',
		welcome_description: 'This is your first visit. We are happy to have you here!',
		username_label: 'Choose a username',
		username_description: 'This can be your own name or a chosen name.',
		username_change_avatar: 'Choose a picture',
		username_disclaimer: 'You can always change your name later!',
		message_example: 'This is what your messages will look like:',
		message_example_text: "Wow, That's cool!",
		consent_text: 'I have read the rules and privacy policy of the {0} hub and accept the terms.',
		enter_hub: 'Save and enter the hub',
		welcome_consent: 'The house rules of this hub have changed',
		welcome_consent_description: 'Please review the terms before entering.',
		keep_pseudonym: 'Leave empty to keep your pseudonym {0}.',
		step: 'Step {0} of {1}',
		submit_error: 'Something went wrong while saving. Please try again.',

		info_abt_pseudonym: 'This hub has given you a random name (pseudonym)',
		info_issue_identity:
			'This pseudonym hides your identity for privacy reasons. But it is not very convenient for others in the hub. Therefore, You can choose a nickname yourself that others in the hub will see',
		info_abt_nickname_use: 'Please note that other participants may also use self-chosen name that need not be their real names.',
		info_abt_choose_later: 'You can also choose a name later, or change it again.',
		info_abt_yivi_room:
			'For certain rooms in this hub, you may be asked to reveal (part of) your identity, via the Yivi app. This gives participants in those rooms certainty about each other.',
		info_misbehave: 'If you misbhehave, you can be banned from this hub.',
		continue: 'Continue',
		success_msg: 'You can explore the rooms and chat with the community!',
		later: "You haven't set your name for this hub yet. You can do this later.",
		update: 'Name updated',
		nickname_placeholder: 'Type here your chosen name ...',
	},
	message: {
		send: 'Send',
		in_reply_to: 'In reply to:',
		editing: 'Editing message',
		edited: 'Edited',
		edited_at: 'Edited {datetime}',
		notification: 'New message in hub',
		upload_file: 'Upload',
		reply: 'Reply',
		reply_in_thread: 'Reply in thread',
		view_thread: 'View thread ({count} reply) | View thread ({count} replies)',
		whisper_to: 'Whisper to',
		whisper_visible_only_to: 'Whisper visible only to',
		only_visible_to_you: 'Only visible to you',
		reply_emoji: 'React',
		emoji_picker: 'Emoji',
		quick_reactions: 'Quick reactions',
		react_with: 'React with {emoji}',
		reaction_toggle: 'React with {emoji}, {count} so far',
		more_reactions: 'More reactions',
		context_menu: 'More actions',
		scheduler: 'Schedule',
		poll: 'Poll',
		videocall: 'Video call',
		announcement_mode: 'Announcement',
		announcement_placeholder: 'Announcement',
		enable_announcement: 'Announcement',
		disable_announcement: 'Disable announcement mode',
		scheduler_closed: 'This scheduler has ended. You are no longer able to respond.',
		pick_option: 'Pick final date',
		close: 'Close',
		reopen: 'Reopen',
		edit: 'Edit',
		pick: 'Pick',
		sign: {
			add_signature: 'Sign',
			heading: 'Signing a message',
			info: "Go 'on the record' by signing your message with your information.",
			warning: 'A signed message can be shared. Do not include personal information and consider other users in this room.',
			selected_attributes: 'Your message will be signed with this information:',
			signed_message_email: 'Signed message (email)',
			signed_message_tooltip: 'Your message will be signed with your email address so others can verify it came from you.',
			send: 'Sign & send',
			edit: 'Sign & edit',
		},
		messageSigned: {
			heading: 'Signed message',
			info: 'A message that is signed with Yivi attributes.',
			verificationStatus: 'Signed messages are a work in progress. They are trustworthy, but are not yet cryptographically verified.',
		},
		delete: {
			cannot_undo: 'You cannot undo this.',
			heading: 'Delete message?',
			user: 'Deleted user',
			beware: 'Beware!',
			file_not_deleted: 'Your message is no longer visible to others after deletion, but the file can still be downloaded by users who saved the link.',
			original_message_deleted: 'Original message deleted',
			message_deleted: 'Deleted message',
			message_removed_by_steward: 'Message deleted by steward',
			reason: 'Reason',
			reason_label: 'Reason for deleting this message (visible to the author)',
		},
		voting: {
			add_option: 'Add option',
			creating_option: 'Creating option...',
			title: 'Title',
			description: 'Description',
			option: 'Option',
			option_number: 'Option {number}',
			location: 'Location',
			enter_title: 'Enter title...',
			enter_option: 'Enter option...',
			enter_description: 'Enter description...',
			enter_location: 'Enter location...',
			allow_multiple_answers: 'Allow multiple answers',
			show_votes_before_voting: 'Show votes before voting',
			show_votes: 'Show votes',
			hide_votes: 'Hide votes',
			plural_votes: 'vote|votes',
			edited: 'edited',
		},
		threads: {
			view_thread: 'view thread',
		},
		forum: {
			add_new_thread: 'Create new post',
			edit_thread: 'Edit post',
			no_threads: 'No posts yet',
			all_posts: 'All posts',
			comments: 'Comments',
			no_comments: 'No comments yet, be the first to reply',
			show_replies: 'Show replies ({0})',
			hide_replies: 'Hide replies',
			upvote: 'Upvote',
			downvote: 'Downvote',
			sort_trending: 'Trending',
			sort_newest: 'Newest',
			sort_top: 'Top',
			title: 'Title',
			description: 'Description',
			help_title: 'Give your post a short, descriptive title',
			help_description: 'Introduce your topic and expand on your post',
		},
	},
	time: {
		today: 'Today',
		yesterday: 'Yesterday',
		daysago: '{0} days ago',
	},
	emoji: {
		clock: 'All',
		smiley: 'Smileys & people',
		bear: 'Animals & nature',
		cup: 'Food & drink',
		basketball: 'Travel & places',
		house: 'Activities',
		lightbulb: 'Lifestyle',
		signs: 'Symbols',
		flag: 'Flags',
	},
	validation: {
		required: '{0} is a required field',
		max_length: '{0} is too long , there should be max {1} characters, but there are {2} ',
		min_length: '{0} is too short, there should be min {1} characters, but there are {2}',
		max_items: 'Too many {0}, there should be max {1} items, but there are {2}',
		min_items: ' Too few {0}, there should be min {1} items, but there are {2}',
		max_value: '{0} is too high, the maximum value is {1}, but there are {2}',
		min_value: '{0} needs to at least have a value of {1}, but there are {2}',
		is_number: '{0} must be a number',
	},
	bubble: {
		organize: 'Organize your hubs by dragging them.',
	},
	roomlibrary: {
		library: 'Files',
		search: 'Search files',
		empty: 'No files yet',
		empty_hint: 'Upload files to share them with everyone in this room.',
		file_count: '0 files (max. {max}) | 1 file (max. {max}) | {count} files (max. {max})',
		select: 'Select',
		select_all: 'Select all',
		delete_selected: 'Delete',
		drop_to_upload: 'Drop files to upload',
		library_full: 'This room already has the maximum of {0} files. Delete files to make room.',
		files_skipped: 'Not all files were added: a room can hold at most {0} files.',
		upload_waiting: 'Waiting',
		uploading: 'Uploading {0}%',
		upload_done: '{0} uploaded',
		upload_failed: 'Upload of {0} failed',
		sign_error: 'File {0} could not be prepared for signing. Please try again.',
		sort: {
			label: 'Sort',
			newest: 'Newest first',
			oldest: 'Oldest first',
			name_asc: 'Name A to Z',
			name_desc: 'Name Z to A',
			largest: 'Largest first',
			uploader: 'Uploader',
		},
		signed: 'Signed by',
		signed_info: 'Signed with',
		used_attribute: 'Used attribute',
		with_value: 'with value',
		sign_file_hash: 'You are about to sign the hash of the file',
		sign_file: 'Sign this file',
		file_details: 'File details',
		delete_file: 'Delete file',
		share_to_timeline: 'Share in the conversation',
		check_file_hash: 'Please check if the hash is the same in the Yivi-app.',
		download_info: 'Download signed information',
		selected_files: '0 | 1 file selected | {count} files selected',
		delete: {
			heading: 'Permanently delete file?',
			content: 'You are about to delete file {0}. <p class="font-bold">Beware! You cannot undo this.</p>',
			multiple_heading: 'Permanently delete these files?',
			multiple_content: 'You are about to delete {0} files. <p class="font-bold">Beware! You cannot undo this.</p>',
			failed: 'File {0} could not be deleted. Please try again.',
			multiple_failed: 'Not all files could be deleted. Please try again.',
		},
		info: {
			name: 'Name',
			filesize: 'Size',
			filetype: 'Type',
			sign: 'Signed',
			uploaded_by: 'Uploaded by',
			uploaded_on: 'Uploaded on',
			type: 'Type:',
			size: 'Size: {0} kb',
			smallsize: '{0} kb',
		},
	},
	attribute: {
		heading: 'Attributes:',
		pbdf: {
			'sidn-pbdf': {
				email: {
					email: 'email',
					domain: 'email domain',
				},
				mobilenumber: {
					mobilenumber: 'phone number',
				},
			},
			gemeente: {
				address: {
					street: 'street',
					houseNumber: 'house number',
					zipcode: 'zipcode',
					city: 'city',
					municipality: 'municipality',
				},
				personalData: {
					fullname: 'fullname',
					firstnames: 'first name',
					surname: 'family name',
					dateofbirth: 'date of birth',
					gender: 'gender',
					over12: 'older than 12',
					over16: 'older than 16',
					over18: 'older than 18',
					over21: 'older than 21',
					over65: 'older than 65',
				},
			},
			pbdf: {
				'surfnet-2': {
					institute: 'surf instituut',
					type: 'surf type',
					id: 'surf id',
					fullname: 'surf fullname',
					firstname: 'surf firstname',
					familyname: 'surf lastname',
					email: 'surf email',
				},
			},
		},
	},
	videocall: {
		appear_as: "You'll appear as",
		chat: 'Chat',
		devices: 'Devices',
		disable_camera: 'Turn off camera',
		duration: 'Duration',
		e2ee_blocked_body:
			'Video calls are end-to-end encrypted, and this browser or session cannot encrypt them. Try a recent version of Chrome, Firefox or Safari, and make sure the site may store data.',
		e2ee_blocked_title: 'Encrypted calls are not available here',
		enable_camera: 'Turn on camera',
		exit: 'Exit',
		exit_fullscreen: 'Exit fullscreen',
		fullscreen: 'Fullscreen',
		fullscreen_tooltip: 'Toggle fullscreen (Shift+F)',
		hide_self_view: 'Hide your own video',
		join: 'Join',
		join_button: 'Join videocall',
		join_tooltip: 'Join videocall',
		leave_call: 'Leave call',
		message_preview: 'Videocall',
		microphone_source: 'Microphone',
		mute_for_me: 'Mute for me',
		mute_microphone: 'Mute microphone',
		no_camera: 'No camera',
		no_microphone: 'No microphone',
		no_speaker: 'No speaker',
		nobody_here: 'No other participants yet',
		participants: 'Participants',
		participants_count: 'Participants ({count})',
		permission_denied_body: 'PubHubs cannot reach your microphone or camera. Allow access through your browser settings, then refresh the devices.',
		permission_denied_title: 'Access blocked',
		permission_pending_body: 'Your browser is asking whether PubHubs may use your microphone and camera. Choose allow to continue.',
		permission_pending_title: 'Allow access',
		permission_screenshare_body:
			'Your browser did not hand over a screen to share. Choose a window or screen when it asks, or allow screen recording for your browser in your system settings.',
		permission_screenshare_title: 'Screen sharing did not start',
		permission_unavailable_body:
			'We could not find a microphone or camera on this device. Connect one and refresh the devices, or join to listen and watch only.',
		permission_unavailable_title: 'No devices found',
		prejoin_title: 'Starting video call',
		refresh_devices: 'Refresh devices',
		self_label: 'You',
		show_self_view: 'Show your own video',
		speaker_source: 'Speaker',
		state: {
			started: 'Videocall started',
			joined: 'Has joined',
			left: 'Has left',
			ended: 'Videocall ended',
			startedended: 'Videocall started',
		},
		start_button: 'Start video call',
		start_screen_share: 'Share your screen',
		start_tooltip: 'Start videocall',
		stop_screen_share: 'Stop sharing your screen',
		undecryptable: 'Cannot decrypt this video',
		unmute_for_me: 'Unmute for me',
		unmute_microphone: 'Unmute microphone',
		video_source: 'Camera',
		you: '(you)',
	},
	roles: {
		details: 'Role details',
		room: 'Room',
		type: 'Type',
		status: 'Status',
		search: 'Search roles',
		empty: 'No roles or stewards found',
		refresh: 'Refresh',
		load_more: 'Load more',
		loading_more: 'Loading...',
		new_invitation: 'New invitation',
		select_placeholder: 'Select an entry from the list to see more details',
		// Types
		type_steward_invitation: 'Steward invitation',
		type_expert_invitation: 'Expert invitation',
		type_steward: 'Steward',
		type_expert: 'Expert',
		type_information: 'Information',
		// Status
		status_disclosed: 'Disclosed',
		status_pending: 'Pending',
		// Filters
		filter_invitations: 'Invitations',
		filter_stewards: 'Stewards',
		filter_experts: 'Experts',
		// Attributes
		disclosed_attributes: 'Disclosed attributes',
		// Actions
		promote_to_steward: 'Promote to steward',
		promote_to_expert: 'Promote to expert',
		reject_invitation: 'Reject invitation',
		demote_steward: 'Demote steward',
		remove_expert: 'Remove expert',
		// Confirmations
		promote_steward_confirm: 'Are you sure you want to promote {name} to steward?',
		promote_expert_confirm: 'Are you sure you want to promote {name} to expert?',
		reject_confirm: 'Are you sure you want to reject the invitation for {name}?',
		demote_confirm: 'Are you sure you want to demote {name} from steward?',
		remove_expert_confirm: 'Are you sure you want to remove expert status from {name}?',
	},
	expert: {
		title: 'Expert',
		expert: 'Expert',
		verify_message: 'Assess message',
		edit_assessment: 'Edit assessment',
		remove_verification: 'Remove assessment',
		assess_message: 'Assess this message',
		verify_message_info: 'As an expert, you can verify this message',
		verify_message_description: 'You are assessing this message with your credentials: {credentials}.',
		verify_sign_info: 'You will sign this assessment with your full name using Yivi.',
		add_context_label: 'Add context',
		verify: 'Verify',
		sign_and_verify: 'Sign and verify',
		sign_and_submit: 'Sign and submit',
		scan_to_sign: 'Scan to sign',
		scan_to_sign_description: 'Scan the QR code with the Yivi app to sign this assessment with your full name.',
		verification_signature_message: 'Expert assessment for message {eventId} with credentials: {credentials}. Note: {note}',
		no_note: 'No note provided',
		// Verification type labels
		verification_type_label: 'What is your assessment?',
		type_verified: 'Verify',
		type_verified_description: 'This information is accurate',
		type_falsified: 'Falsify',
		type_falsified_description: 'This information is incorrect',
		type_context: 'Add context',
		type_context_description: 'Add nuance or additional information',
		// Status messages per type
		verified_by: 'Verified by {expert}',
		verified_and_signed_by: 'Verified and signed by {expert}',
		falsified_by: 'Falsified by {expert}',
		falsified_and_signed_by: 'Falsified and signed by {expert}',
		context_by: 'Context added by {expert}',
		context_and_signed_by: 'Context added and signed by {expert}',
		assessed_at: 'Assessed at {date}',
		verified_at: 'Verified at {date}',
		assign_expert: 'Assign expert status',
		assign_expert_confirm: 'Assign expert status to {user}?',
		assign_expert_note: 'This user will be able to verify messages in this room. They need to set up their expert profile with their credentials first.',
		remove_expert: 'Remove expert status',
		// Profile
		edit_profile: 'Expert profile',
		profile_description: 'Set up your expert credentials. These will be used when you assess messages.',
		profile_required: 'Expert profile required',
		profile_required_description:
			'You need to set up your expert profile before you can assess messages. Your profile includes your credentials and other information that will be shown when you assess messages.',
		set_profile: 'Set up profile',
		credentials: 'Credentials',
		specializations: 'Specializations (comma-separated)',
		institution: 'Institution',
		// Display labels
		credentials_label: 'Credentials:',
		specializations_label: 'Specializations:',
		note_label: 'Note:',
		sources_label: 'Sources (one per line)',
		sources_display_label: 'Sources:',
		source_placeholder: 'https://example.com/source',
		// Failures
		verify_failed: 'Your assessment could not be sent. Please try again.',
		remove_verification_failed: 'Your assessment could not be removed. Please try again.',
		profile_save_failed: 'Your expert profile could not be saved. Please try again.',
	},
};

export { en };
